// A2F-546 (anonymous variant) / A2F-549 (logged-in variant) — top nav
// for the onboarding page at `/`.
//
// Accent scope narrowed (feedback 2026-04-19): logo box, PRO badge,
// avatar, and Sign in button use brand-orange. Detected accent only
// threads to DocumentPreview.

import { useState } from 'react'
import { ChevronDown, LogOut } from 'lucide-react'
import { Link, NavLink } from 'react-router-dom'
import type { Session } from '@supabase/supabase-js'
import { useAuth } from '../../lib/AuthContext'

interface TopNavProps {
  session: Session | null
}

export function TopNav({ session }: TopNavProps) {
  return (
    <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md grid place-items-center bg-brand-orange text-white text-[11px] font-bold">
            C
          </div>
          <span className="text-sm font-semibold tracking-wider text-brand-charcoal">
            CHRONA
          </span>
          <span className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded text-brand-orange bg-brand-orange/10">
            PRO
          </span>
        </Link>
        {session ? (
          <LoggedInNav session={session} />
        ) : (
          <Link
            to="/configure"
            className="text-sm font-medium text-brand-orange hover:text-brand-orange/80"
          >
            Sign in →
          </Link>
        )}
      </div>
    </header>
  )
}

function LoggedInNav({ session }: { session: Session }) {
  const { signOut } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)

  const displayName =
    (session.user.user_metadata?.display_name as string | undefined) ??
    session.user.email ??
    'Account'
  const initial = displayName.charAt(0).toUpperCase()

  return (
    <div className="flex items-center gap-5">
      <NavLink
        to="/install"
        className="text-sm font-medium text-brand-charcoal hover:text-brand-orange"
      >
        Install
      </NavLink>
      <NavLink
        to="/configure"
        className="text-sm font-medium text-brand-charcoal hover:text-brand-orange"
      >
        Customize
      </NavLink>
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2 rounded-full border border-slate-200 px-2 py-1 hover:border-slate-300"
        >
          <span className="h-6 w-6 rounded-full bg-brand-orange grid place-items-center text-[11px] font-bold text-white">
            {initial}
          </span>
          <span className="text-xs font-medium text-brand-charcoal max-w-[140px] truncate">
            {displayName}
          </span>
          <ChevronDown size={14} className="text-slate-500" />
        </button>
        {menuOpen ? (
          <div className="absolute right-0 top-10 w-48 rounded-lg border border-slate-200 bg-white shadow-lg py-1">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                void signOut()
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-charcoal hover:bg-slate-50"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
